import type { IOnboardingLeaveType, IOnboardingLocation } from './types'
import { normalizeIntervalEnum } from './types'
import type { ILeaveLawEntry, ILeaveLawSuggestion, LeaveLawsResponse } from '@vacationtracker/shared/types/leave-law'
import { findLeaveLaw, findLeaveLawSuggestionForLeaveType, statutoryDaysPerYear } from '@vacationtracker/shared/functions/leave-laws'

/**
 * Wraps the shared rule so the wizard resolves exactly what the backend does: aliases for Paid Time
 * Off and Sick Leave, the feed's own name exactly for every other entitlement.
 */
export const matchToApiSuggestion = (dbName: string, suggestions: ILeaveLawSuggestion[]): ILeaveLawSuggestion | undefined =>
  findLeaveLawSuggestionForLeaveType({ suggestedLeaveTypes: suggestions } as ILeaveLawEntry, dbName)

/**
 * The quota applied once, as suggestions leave this layer, so no consumer can read the feed's raw
 * 0 for an entitlement the jurisdiction does not mandate - which is what the backend already
 * stores, and so what the wizard has to propose.
 */
const withStatutoryDays = (suggestion: ILeaveLawSuggestion): ILeaveLawSuggestion => ({ ...suggestion, daysPerYear: statutoryDaysPerYear(suggestion) })

interface GetApiSuggestionsOptions {
  onlyDefault?: boolean
  signupLocationId?: string
}

/**
 * The statutory entitlements for the company's locations, deduped by key.
 *
 * Entry selection is delegated to the shared findLeaveLaw so the wizard proposes exactly what the
 * backend wrote. The signup location goes first, so where two locations disagree the place the
 * company actually signed up from wins.
 */
export const getApiSuggestionsForLocations = (
  apiData: LeaveLawsResponse,
  locations: IOnboardingLocation[],
  { onlyDefault = true, signupLocationId }: GetApiSuggestionsOptions = {}
): ILeaveLawSuggestion[] => {
  const ordered = signupLocationId ? [...locations].sort((a, b) => Number(b.id === signupLocationId) - Number(a.id === signupLocationId)) : locations

  const seen = new Set<string>()
  const result: ILeaveLawSuggestion[] = []

  for (const location of ordered) {
    const entry = findLeaveLaw(apiData, location)
    if (!entry) continue
    for (const suggestion of entry.suggestedLeaveTypes) {
      if ((onlyDefault && !suggestion.isDefault) || seen.has(suggestion.key)) continue
      seen.add(suggestion.key)
      result.push(withStatutoryDays(suggestion))
    }
  }

  return result.sort((a, b) => (b.isLegallyRequired ? 1 : 0) - (a.isLegallyRequired ? 1 : 0) || a.sortOrder - b.sortOrder)
}

export const reconcileWithNewLocation = (existingTypes: IOnboardingLeaveType[], newLoc: IOnboardingLocation, apiData: LeaveLawsResponse): IOnboardingLeaveType[] => {
  const suggestions = getApiSuggestionsForLocations(apiData, [newLoc], { onlyDefault: false })
  if (suggestions.length === 0) return existingTypes

  let changed = false
  const result = existingTypes.map((lt) => {
    if (lt.overrides.some((o) => o.locationId === newLoc.id)) return lt

    const suggestion = matchToApiSuggestion(lt.name, suggestions)
    if (!suggestion) return lt

    const locDays = suggestion.hasUnlimitedDays ? 0 : suggestion.daysPerYear

    const normalizedInterval = normalizeIntervalEnum(suggestion.shortestLeaveIntervalEnum)

    if (
      locDays === lt.daysPerYear &&
      suggestion.hasUnlimitedDays === lt.hasUnlimitedDays &&
      suggestion.isApprovalRequired === lt.isApprovalRequired &&
      normalizedInterval === lt.shortestLeaveIntervalEnum
    )
      return lt

    changed = true
    return {
      ...lt,
      overrides: [
        ...lt.overrides,
        {
          locationId: newLoc.id,
          locationName: newLoc.name,
          daysPerYear: locDays,
          hasUnlimitedDays: suggestion.hasUnlimitedDays,
          isApprovalRequired: suggestion.isApprovalRequired,
          allowHalfDays: suggestion.allowHalfDays,
          shortestLeaveIntervalEnum: normalizedInterval,
        },
      ],
    }
  })
  return changed ? result : existingTypes
}

export const cleanupLeaveTypesForRemovedLocation = (leaveTypes: IOnboardingLeaveType[], removedLocationId: string): IOnboardingLeaveType[] =>
  leaveTypes.map((lt) => ({
    ...lt,
    appliesTo: lt.appliesTo.filter((id) => id !== removedLocationId),
    overrides: lt.overrides.filter((o) => o.locationId !== removedLocationId),
  }))
